import {
  Check, MailOpen, MousePointerClick,
} from 'lucide-react'
import type {
  LucideIcon,
} from 'lucide-react'
import type {
  ReferralInviteStatus,
} from '../client'
import type {
  ReferralProgram,
} from '../utils/referralRouting'

/** Shared building blocks for the referral surfaces (ReferralStrip on the
 * Integrations page and the full-network ReferralPage at /refer), so the two
 * views render invites, statuses, and avatars identically. */

export const AVATAR_TINTS = [
  'bg-indigo-100 text-indigo-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
]

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** The `source` an invite send records for each programme. Must match the
 * backend's `invite_source_for_program` — `refer` is the value that grants the
 * invitee's $200, so the two sides cannot drift. */
export const INVITE_SOURCE: Record<ReferralProgram, string> = {
  standard: 'refer_standard',
  ambassador: 'refer',
}

// The marketing promo page (landing site) that explains the $1,000-getaway
// referral reward in full. Lives on www.catchagent.ai; opened in a new tab so
// the user keeps their place in the app.
export const REFERRAL_PROMO_URL =
  'https://www.catchagent.ai/our-biggest-referral-reward-yet'

/** Render a US-cent amount as "$200" / "$199.50" — mirrors the backend's
 * `_format_dollars` so every referral surface describes the same amount. */
export function formatDollars(cents: number): string {
  const dollars = cents / 100
  return Number.isInteger(dollars)
    ? `$${dollars.toLocaleString('en-US')}`
    : `$${dollars.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
}

export function initials(name: string | null | undefined, email: string): string {
  const source = (name ?? '').trim() || email
  const [first = '', second = ''] = source.split(/\s+/).filter(Boolean)
  const combined = (first.charAt(0) + second.charAt(0)).trim()
  return (combined || source.slice(0, 2)).toUpperCase()
}

/** A person the user has already invited (shown in the "Invited" sections). */
export type InvitedContact = {
  email: string
  name: string | null
  status: ReferralInviteStatus
}

// How each funnel stage renders as a status pill. The ladder escalates visually
// — neutral when merely sent, sky once opened, emerald once the CTA is clicked —
// so a glance down the list reads as engagement. Opened/Clicked only ever appear
// when email-open tracking is enabled for the sender; otherwise everything stays
// "Invited" (graceful degradation).
export const STATUS_PILL: Record<
  ReferralInviteStatus,
  { label: string; title: string; icon: LucideIcon; className: string }
> = {
  INVITED: {
    label: 'Invited',
    title: 'Invite sent',
    icon: Check,
    className: 'bg-gray-50 text-gray-500 border-gray-200',
  },
  OPENED: {
    label: 'Opened',
    title: 'Opened the invite email',
    icon: MailOpen,
    className: 'bg-sky-50 text-sky-600 border-sky-200',
  },
  CLICKED: {
    label: 'Clicked',
    title: 'Clicked through from the invite',
    icon: MousePointerClick,
    className: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  },
}

// Merge a fresh server fetch over local state: server rows win (they carry the
// latest open/click status), while optimistic locals the server hasn't surfaced
// yet (an invite sent moments ago) stay pinned ahead of them.
export function mergeInvited(
  prev: InvitedContact[],
  incoming: InvitedContact[],
): InvitedContact[] {
  const incomingEmails = new Set(incoming.map((c) => c.email.toLowerCase()))
  const extras = prev.filter((c) => !incomingEmails.has(c.email.toLowerCase()))
  return [...extras, ...incoming]
}
